import { OffthreadVideo, Sequence } from 'remotion';

import { Box, Heading3, Heading4, u } from '@ui';

import { useOptions } from './context';
import { VideoSkeleton } from './VideoSkeleton';

const VIDEO_TYPE = {
    CURRENT: 'current',
    COMPARISON: 'comparison',
    LIVE: 'live',
} as const;

type VideoType = (typeof VIDEO_TYPE)[keyof typeof VIDEO_TYPE];

type VideoWithMetadata = {
    url: string;
    name: string;
    type: VideoType;
    id: string;
    view: number;
};

export type Video = VideoWithMetadata | undefined;

export const FPS = {
    camera: 255,
    video: 25,
};

const rawVideoStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
} as const;

export const VideoCell = ({ video, diff }: { video: VideoWithMetadata; diff: number }) => {
    const { showSkeleton } = useOptions();
    /**
     * @var `diff` is activeVideo segment - comparisonVideo segment.
     *
     * It is only being applied to comparisonVide, `diff` for active video is always 0,
     * because we base scrubber on active video.
     */

    // If offset is positive, we start the comparison video later by that amount of frames
    // Example: if positiveOffset is 30, then comparison video at the global frame 30 starts at local frame 0.
    const positiveOffset = Math.max(0, diff);

    // If offset is negative, we start the comparison video at frame 0, cut off that amount of frames.
    // Example: if negativeOffset is 30, then comparison video at the global frame 0 starts at local frame 30.
    const negativeOffset = Math.max(0, -diff);
    const adjustedNegativeOffset = Math.round(negativeOffset * (FPS.camera / FPS.video));

    return (
        <Box height="100%" width="100%" position="relative">
            <Sequence from={positiveOffset} layout="none">
                {video.type === VIDEO_TYPE.LIVE ? (
                    <img src={video.url} alt={`Live ${video.view}`} style={rawVideoStyle} />
                ) : (
                    <OffthreadVideo
                        src={video.url}
                        muted
                        style={rawVideoStyle}
                        trimBefore={adjustedNegativeOffset}
                        playbackRate={FPS.camera / FPS.video}
                    />
                )}
            </Sequence>
            {showSkeleton && video.view && <VideoSkeleton cameraView={video.view} />}
            <Box position="absolute" top={u(1)} left={u(1)}>
                <Heading4 color="white">{video.type}</Heading4>
                <Heading3 color="white">{video.name}</Heading3>
            </Box>
        </Box>
    );
};
